import React, { useCallback, useContext, useState } from "react";
import { displayForResult } from "common-client/utils/search";
import { RecentSearch } from "common-client/utils/search";
import { spacing } from "../../stitches.config";
import { FlexRow } from "../Common/__styles__/Layout";
import { EmptyState } from "../Common/EmptyState";
import { Icon } from "../Common/Icons/LucideIcons";
import { Body } from "../Common/Typography";
import { CommonContentIconWrapper } from "../Inputs/__styles__/SelectionCard";
import { SelectionCard } from "../Inputs/SelectionCard";
import { SearchContext } from "./SearchContext";

const OmniboxRecentSearchItem = ({
  search,
  refreshRecentSearches,
}: {
  search: RecentSearch;
  refreshRecentSearches: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { removeRecentSearch } = useContext(SearchContext);
  const { title, description, iconName } = displayForResult({
    result: search.clickedResult,
    showMatchReason: false,
  });
  return (
    <SelectionCard.Item
      key={search.clickedResult.id}
      size="compact"
      borderStyle="ghost"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      value={search.clickedResult}
    >
      <FlexRow gap="m">
        <SelectionCard.CommonContent
          iconName={iconName}
          title={title}
          description={description}
        />
        {/* this should be an ActionButton but we're already inside a button here */}
        <CommonContentIconWrapper
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            removeRecentSearch({
              resultId: search.clickedResult.id,
              resultType: search.clickedResult.__typename,
            });
            refreshRecentSearches();
          }}
          style={{ opacity: isHovered ? 1 : 0 }}
        >
          <Icon
            aria-label="Remove recent search"
            iconName="x"
            size={16}
            color="contentSecondary"
          />
        </CommonContentIconWrapper>
      </FlexRow>
    </SelectionCard.Item>
  );
};

export const OmniboxRecentSearches = ({
  expandPlaceholders,
}: {
  expandPlaceholders: boolean;
}) => {
  const { getRecentSearches, handleSearchResultClick } =
    useContext(SearchContext);
  const [updateCounter, setUpdateCounter] = useState(0);

  const refreshRecentSearches = useCallback(() => {
    setUpdateCounter(n => n + 1);
  }, [setUpdateCounter]);

  const recentSearches = getRecentSearches();
  if (recentSearches.length === 0) {
    return expandPlaceholders ?
        <EmptyState
          icon="no-activity-beach"
          paddingTop={spacing["2xl"].value}
          title="No activity"
          body="Recent searches will appear here."
        />
      : <EmptyState
          compact
          title="No recent searches"
          paddingTop={0}
          paddingBottom={0}
        />;
  }
  return (
    <SelectionCard.Group
      id={`recent-searches-${updateCounter}`}
      direction="vertical"
      spacing="compact"
      handleSelection={handleSearchResultClick}
    >
      <Body
        type="emphasis"
        size="small"
        style={{ padding: `${spacing.s} ${spacing.m} 0` }}
      >
        Recent Searches
      </Body>
      {recentSearches.map(search => (
        <OmniboxRecentSearchItem
          key={`${search.clickedResult.id}-${search.clickedResult.__typename}`}
          search={search}
          refreshRecentSearches={refreshRecentSearches}
        />
      ))}
    </SelectionCard.Group>
  );
};
